import "./global.scss";
import { h, Fragment } from "preact";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useCommand, useForceUpdate } from "./hooks";
import { AppGlobalState } from "./AppGlobalState";
import { Router, route as preactRoute } from "preact-router";
import { Home } from "./pages/home";
import { Products } from "./pages/products";
import "../views/styles.scss";
import { StellaBIO } from "./pages/stella-bio";
import { NotFound } from "./pages/not-found";
import { GenerateSerialNumber } from "./pages/generate-serial-number";
import { PrintSerialNumber } from "./pages/print-serial-number";
import { Order } from "./pages/order";
import { OrderEdit } from "./pages/order/edit";
import { OrderRevision } from "./pages/order/revision";
import { toDataURL } from "qrcode";
import { LunaEMG } from "./pages/luna-emg";
import { AuthenticationService } from "./services/AuthenticationService";
import { EGZOTechAgentService } from "./services/EGZOTechAgentService";
import { environment } from "./enviroment";
import { EGZOTechAgentManager } from "./services/EGZOTechAgentManager";
import { getAPIVersion, getManufacturingConfigurations, ManufacturingConfigurations } from "./api/product-api";
import { ProductTypeList } from "./pages/product-type-list";
import { ProductList } from "./pages/product-list";
import { LabelPage } from "./pages/label";
import { ProductTypeRevisionPage } from "./pages/product-type-revision";
import { ProductTypePage } from "./pages/product-type";
import { BatchLotRegister } from "./pages/batch-lot-register";
import { MezosSIT } from "./pages/mezos-sit";
import { resolvePath } from "./api";
import { version } from "../package.json";
import { SidraLEG } from "./pages/sidra-leg";
import { MeissaOT } from "./pages/miessa-ot";
import { ProductTypeNew } from "./pages/product-type/new";

declare const google: typeof import("google-one-tap");

export const applicationState = new AppGlobalState();

const url = new URL(location.href);

if (url.searchParams.get("advanced") !== null) {
    applicationState.showAdvancedOptions = true;
}

if (url.searchParams.get("agent")) {
    localStorage.setItem("agent", url.searchParams.get("agent")!);
}

export const baseUrl = url.pathname.startsWith("/production-automation") ? "/production-automation" : ""

export function route(url: string, replace?: boolean) {
    return preactRoute(`${baseUrl}${url}`, replace);
}

export const authenticationService = new AuthenticationService();
export const egzotechAgentService = new EGZOTechAgentService(authenticationService);
export const egzotechAgentManager = new EGZOTechAgentManager(egzotechAgentService);

export let manufacturingConfigurations: ManufacturingConfigurations;

interface FetchInfo {
    ok: boolean;
    status: number;
    statusText: string;
    headers: Headers;
    error?: Error;
}

function useFetch(url: string, format: "arrayBuffer", init?: RequestInit): readonly [ArrayBuffer | null, FetchInfo | null];
function useFetch(url: string, format: "blob", init?: RequestInit): readonly [Blob | null, FetchInfo | null];
function useFetch(url: string, format: "formData", init?: RequestInit): readonly [FormData | null, FetchInfo | null];
function useFetch<T = any>(url: string, format: "json", init?: RequestInit): readonly [T, FetchInfo | null];
function useFetch(url: string, format: "text", init?: RequestInit): readonly [string | null, FetchInfo | null];
function useFetch(url: string, format: "arrayBuffer" | "blob" | "formData" | "json" | "text", init?: RequestInit): readonly [any, FetchInfo | null] {
    const [response, setResponse] = useState({ r: null, f: null } as { r: any, f: FetchInfo | null });
    useMemo(async () => {
        let response: Response;

        try {
            response = await fetch(url, init);
        }
        catch(ex: any) {
            setResponse({
                r: null,
                f: {
                    ok: false,
                    status: 0,
                    statusText: "Fetch error",
                    headers: null!,
                    error: ex instanceof Error ? ex : new Error(ex.toString())
                }
            });
            return;
        }

        try {
            setResponse({ r: await response[format](), f: response });
        }
        catch(ex: any) {
            setResponse({
                r: null,
                f: {
                    ok: response.ok,
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                    error: ex instanceof Error ? ex : new Error(ex.toString())
                }
            });
        }
    }, []);

    return [response.r, response.f] as const;
}

export function App() {
    const forceUpdate = useForceUpdate();

    authenticationService.forceUpdateApp = forceUpdate;

    const state = applicationState;
    state.forceUpdateApp = forceUpdate;
    const [scanPasteQr, setScanPasteQr] = useState("");
    const [checkIfLoggedState, setCheckIfLoggedState] = useState("checking");
    const [agentState, setAgentState] = useState("");
    const [agentStateType, setAgentStateType] = useState("");
    const [apiVersion, setAPIVersion] = useState({} as { version?: string, build?: number, environment?: string })

    const [localAgents, localAgentsRequest] = useFetch<string[]>("http://localhost:6578/local-agents", "json");
    const agents = useMemo(() => (localStorage.getItem("agent") ?? "").split(",").map(v => v.trim()).filter(v => v), []);

    const checkAgentState = useCallback(async (agent: string) => {
        if (agentState === "...") {
            return;
        }

        setAgentState("...");
        setAgentStateType("");

        if (!egzotechAgentService.isConnected) {
            setAgentState("Brak połączenia z serwerem");
            setAgentStateType("error");
            return;
        }

        let agents;

        try {
            agents = await egzotechAgentService.getAgentList();
        }
        catch(ex) {
            console.error(ex);
            setAgentState("Brak połączenia z serwerem");
            setAgentStateType("error");
            return;
        }

        if (agents.indexOf(agent) < 0) {
            setAgentState("Brak połączenia ze stacją roboczą");
            setAgentStateType("warning");
            return;
        }

        const registeredAgent = egzotechAgentManager.getAgent(agent)!;
        const statusLines = [];

        statusLines.push("Połączono ze stacją roboczą");
        setAgentStateType("success");

        if (registeredAgent.modules.indexOf("label-printer") >= 0) {
            let response;

            try {
                response = await egzotechAgentService.labelPrinter.getPrinterInfo(agent);
            }
            catch(ex) {
                console.error(ex);
                setAgentState("Brak połączenia ze stacją roboczą");
                setAgentStateType("warning");
                return;
            }

            if (response.portName) {
                statusLines.push("+ Połączono z drukarką");
            }
            else {
                statusLines.push("- Brak połączenia z drukarką");
            }
        }

        if (registeredAgent.modules.indexOf("stella-flash") >= 0) {
            statusLines.push("+ Programowanie StellaBIO");
        }

        if (registeredAgent.modules.indexOf("mezos-flash") >= 0) {
            statusLines.push("+ Programowanie MezosSIT i akcesoriów");
        }

        if (registeredAgent.modules.indexOf("nfc-mfultralight") >= 0) {
            statusLines.push("+ Programowanie NFC");
        }

        setAgentState(statusLines.join("\n"));
    }, [agentState]);

    useEffect(() => {
        toDataURL("https://production.internal.egzotech.com/qr.php", (e, url) => { setScanPasteQr(url); });
    }, []);

    useEffect(() => {
        getAPIVersion().then(v => setAPIVersion(v));
    }, [])

    useEffect(() => {
        if (localAgents && localAgentsRequest) {
            for (let i = 0; i < localAgents.length; i++) {
                if (!agents.includes(localAgents[i])) {
                    agents.push(localAgents[i]);
                }
            }

            forceUpdate();
        }

        setCheckIfLoggedState("checking");
        egzotechAgentService.connectToRegistry().then(() => {
            for (let i = 0; i < agents.length; i++) {
                egzotechAgentManager.addAgent(agents[i]);
            }
        });

        (async () => {
            try {
                await authenticationService.checkIfLogged();
                manufacturingConfigurations = await getManufacturingConfigurations();
            }
            finally {
                setCheckIfLoggedState("done");
            }
        })();
    }, [localAgentsRequest]);

    useEffect(() => {
        authenticationService.onLogin = async () => {
            manufacturingConfigurations = await getManufacturingConfigurations();
            setCheckIfLoggedState("done");
        };

        return () => {
            delete authenticationService.onLogin;
        }
    }, []);

    if (checkIfLoggedState === "checking") {
        return <div class="app">
            <div class={"top-bar"}>
                <div class="egzotech-logo"></div>
                <div><button type="button" class="icon flat borderless" onClick={() => history.back()}>arrow_back</button></div>
                <div style="flex: 1 1 0;"></div>
            </div>
            <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; height: 100%">
                <div>Ładowanie...</div>
                <div class="loader"></div>
            </div>
        </div>;
    }

    return <div class="app">
        {!authenticationService.currentlyLoggedInUser ? <>
            <div class={"top-bar"}>
                <div class="egzotech-logo"></div>
                <div><button type="button" class="icon flat borderless" onClick={() => history.back()}>arrow_back</button></div>
                <div style="flex: 1 1 0;"></div>
                <div ref={(node) => {
                    if (node) {
                        google.accounts.id.renderButton(node, {
                            type: 'standard',
                            theme: 'filled_black',
                            size: 'large',
                            shape: 'pill',
                            locale: 'pl_PL',
                            width: 250
                        });
                    }
                }}></div>
            </div>
            <Router>
                <Home path={baseUrl}/>
            </Router>
        </> : [<div class="top-bar">
            <label class="button flat icon borderless" for="drawer-visible-controller">menu</label>
            <div><button type="button" class="icon flat borderless" onClick={() => history.back()}>arrow_back</button></div>
            <div style="flex: 1 1 0;"></div>
            {agents.map(v =>
                <div class={"chip secondary tooltip tooltip-pre tooltip-" + agentStateType} data-tooltip={agentState} onMouseEnter={() => checkAgentState(v)}>{v}</div>
            )}
            <label class="button" for="popup-visible-scan-paste">{"Scan&Pack"}<span class="material-icons" style="margin-left: 5px">qr_code</span></label>
            <div>
                <input type="checkbox" class="popup-visible" id="popup-visible-scan-paste"/>
                <div class="popup" style="z-index: 100">
                    <label class="background" for="popup-visible-scan-paste"></label>
                    <div class="content" style="display: flex; flex-direction: column; padding: 10px; width: 300px; height: 250px;">
                        <div style="display: flex; align-items: center;">
                            <div style="flex: 1 1 0; font-size: 14px">Zeskanuj aby przejść do {"Scan&Pack"}</div>
                            <label for="popup-visible-scan-paste" class="button icon flat" style="margin-left: 10px; align-self: flex-end;">close</label>
                        </div>
                        <div style="flex: 1 1 0; display: flex; justify-content: center; align-items: center;">
                            <img src={scanPasteQr}/>
                        </div>
                    </div>
                </div>
            </div>
            <input type="checkbox" class="menu-visible" id="menu-visible-controller"/>
            <div class="menu">
                <label class="background" for="menu-visible-controller"></label>
                <div class="content" style="left: -10px;">
                    <label for="menu-visible-controller" onClick={() => {
                        authenticationService.logout();
                    }}><span class="icon">logout</span>Wyloguj</label>
                </div>
            </div>
            <label class="user-badge" for="menu-visible-controller">
                <div class="name">{authenticationService.currentlyLoggedInUser!.name}</div>
                <img src={authenticationService.currentlyLoggedInUser!.picture} width="40" height="40" style="border-radius: 40px"/>
            </label>
        </div>,
        <div style="margin-top: 52px"/>,
        <Router>
            <Products path={baseUrl}/>
            <LunaEMG path={`${baseUrl}/luna-emg/:rest*`}/>
            <StellaBIO path={`${baseUrl}/stella-bio/:rest*`}/>
            <MezosSIT path={`${baseUrl}/mezos-sit/:rest*`}/>
            <SidraLEG path={`${baseUrl}/sidra-leg/:rest*`}/>
            <MeissaOT path={`${baseUrl}/meissa-ot/:rest*`}/>
            <LabelPage path={`${baseUrl}/label/:rest*`}/>
            <GenerateSerialNumber path={`${baseUrl}/generate-serial-number/:type`}/>
            <PrintSerialNumber path={`${baseUrl}/print-serial-number/:type`}/>
            <OrderRevision path={`${baseUrl}/order/revision/:id`}/>
            <OrderEdit path={`${baseUrl}/order/edit/:id`}/>
            <Order path={`${baseUrl}/order/:id?`}/>
            <Order path={`${baseUrl}/order/:id/:rev?`}/>
            <ProductTypeList path={`${baseUrl}/product-type-list`}/>
            <ProductList path={`${baseUrl}/product-list`}/>
            <ProductTypeRevisionPage path={`${baseUrl}/product-type-revision/:id?`}/>
            <ProductTypePage path={`${baseUrl}/product-type/:id?`}/>
            <ProductTypeNew path={`${baseUrl}/product-type/new`}/>
            <ProductTypeNew path={`${baseUrl}/product-type/edit/:id?`}/>
            <BatchLotRegister path={`${baseUrl}/batch-lot-register`}/>
            <NotFound default/>
        </Router>,
        <div>
            <input type="checkbox" class="drawer-visible" id="drawer-visible-controller"/>
            <div class="drawer">
                <label class="background" for="drawer-visible-controller"></label>
                <div class="content">
                    <div style="margin-top: 60px"><img src={resolvePath("images/logo_color_outline_horizontal.svg")} style="margin-top: 10px"/></div>
                    <div class="vertical-menu">
                        <label for="drawer-visible-controller" onClick={() => route("/")}><span class="icon">build</span>Produkcja</label>
                        <div class="separator"></div>
                        <label for="drawer-visible-controller" onClick={() => route("/product-type-list")}><span class="icon">storage</span>Rodzaje Produktów</label>
                        <label for="drawer-visible-controller" onClick={() => route("/product-list")}><span class="icon">devices_other</span>Produkty</label>
                        <label for="drawer-visible-controller" onClick={() => route("/order")}><span class="icon">list_alt</span>Zestawienia</label>
                        <div class="separator"></div>
                        <label for="drawer-visible-controller" onClick={() => route("/label/group")}><span class="icon">dynamic_feed</span>Zestawy naklejek</label>
                        <label for="drawer-visible-controller" onClick={() => route("/label")}><span class="icon">confirmation_number</span>Naklejki</label>
                        <label for="drawer-visible-controller" onClick={() => route("/label/template")}><span class="icon">texture</span>Szablony naklejek</label>
                        <label for="drawer-visible-controller" onClick={() => route("/label/files")}><span class="icon">perm_media</span>Pliki naklejek</label>
                        {/* <div>Save as...</div>
                        <div class="separator"></div>
                        <div><span class="icon">close</span>Close</div>
                        <div>Quit</div> */}
                    </div>
                    <div style="flex: 1 1"/>
                    <div style="padding: 10px; padding-bottom: 2px; color: #9f9f9f">Wersja aplikacji: {version}</div>
                    <div style="padding: 10px; padding-top: 2px; color: #9f9f9f">Wersja API: {apiVersion.version} build {apiVersion.build} ({apiVersion.environment === "production" ? "prod" : "dev"})</div>
                </div>
            </div>
        </div>]}
    </div>;
}
